import {Component, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {DxDataGridModule, DxFormModule, DxTemplateModule} from 'devextreme-angular';
import {DxDataGridTypes} from 'devextreme-angular/ui/data-grid';
import {GridPageComponent, FilterConfig} from "../../../common/grid-page.component";
import {MissedOpportunity, OrderAppointmentStatus, OrderGridViewDto, OrderWmStatus, PreProductionStatus, ProductionStatus} from "@services";
import {FilterPanelComponent} from "../../../../components/library/filter-panel/filter-panel.component";
import EnumUtils from "../../../../shared/enum-utils";
import {PreProductionStatusPipe} from "../../../../pipes/pre-production-status.pipe";
import {ProductionStatusPipe} from "../../../../pipes/production-status.pipe";
import {OrderAppointmentStatusPipe} from "../../../../pipes/order-appointment-status.pipe";
import {MissedOpportunityPipe} from "../../../../pipes/missed-opportunity.pipe";
import {EasternTimePipe} from "../../../../pipes/eastern-time.pipe";
import {DATE_TIME_FORMAT} from "../../../../shared/constants";

@Component({
    selector: 'app-orders-header',
    standalone: true,
    imports: [
        CommonModule,
        DxDataGridModule,
        DxFormModule,
        DxTemplateModule,
        FilterPanelComponent,
        RouterLink,
        PreProductionStatusPipe,
        ProductionStatusPipe,
        OrderAppointmentStatusPipe,
        MissedOpportunityPipe,
        EasternTimePipe
    ],
    providers: [PreProductionStatusPipe, ProductionStatusPipe, OrderAppointmentStatusPipe, MissedOpportunityPipe],
    templateUrl: './orders-header.component.html',
    styles: [
        `.grid {
            height: calc(100vh - 140px);
        }`
    ]
})
export class OrdersHeaderComponent extends GridPageComponent<OrderGridViewDto> {
    private router = inject(Router);
    private preProductionStatusPipe = inject(PreProductionStatusPipe);
    private productionStatusPipe = inject(ProductionStatusPipe);
    private orderAppointmentStatusPipe = inject(OrderAppointmentStatusPipe);
    private missedOpportunityPipe = inject(MissedOpportunityPipe);

    readonly AppointmentStatus = OrderAppointmentStatus;

    dataSource = this.dataSources.orders();
    dateTimeFormat = DATE_TIME_FORMAT;
    statusOptions = EnumUtils.getEnumOptions(OrderWmStatus);
    preProductionStatusOptions = EnumUtils.getEnumOptions(PreProductionStatus, (v) =>
        this.preProductionStatusPipe.transform(v),
    );
    productionStatusOptions = EnumUtils.getEnumOptions(ProductionStatus, (v) =>
        this.productionStatusPipe.transform(v),
    );
    appointmentStatusOptions = EnumUtils.getEnumOptions(OrderAppointmentStatus, (v) =>
        this.orderAppointmentStatusPipe.transform(v),
    );

    missedOpportunityHeaderFilter = {
        dataSource: EnumUtils.getEnumOptions(MissedOpportunity).map(({value}) => ({
            text: this.missedOpportunityPipe.transform(value),
            value: [this.gridFieldName('missedOpportunities'), 'contains', value],
        })),
    };

    protected override exportName: string = "Orders";

    protected override filterConfig: FilterConfig[] = [
        {field: 'salesDocument', op: 'contains'},
        {field: 'documentDate', op: '>='},
        {field: 'logisticalGroup', op: 'contains'},
        {field: 'deliveryNumber', op: 'contains'},
        {field: 'stagingArea', op: 'contains'},
        {field: 'overallWmStatus', op: '='},
        {field: 'appointmentStatus', op: '='},
        {field: 'appointment', op: '>='},
        {field: 'mdt', op: '>='},
        {field: 'sourceLocationAppointmentDate', op: '>='},
        {field: 'destinationLocationAppointmentDate', op: '>='}
    ];

    constructor() {
        super();
        this.init(OrderGridViewDto);
    }

    override async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        const {data} = e;
        await this.router.navigate([`/outbound/orders/details/${data.salesDocument}`]);
    }

    protected getPreProductionBadgeClass(status: PreProductionStatus): string {
        return EnumUtils.getPreProductionStatusBadgeClass(status);
    }

    protected getProductionBadgeClass(status: ProductionStatus | null): string | null {
        return status ? EnumUtils.getProductionStatusBadgeClass(status) : null;
    }

    protected getAppointmentStatusBadgeClass(status: OrderAppointmentStatus): string {
        return EnumUtils.getOrderAppointmentStatusBadgeClass(status);
    }

    calculateMissedOpportunityCellValue = (order: OrderGridViewDto): string =>
        (order.missedOpportunities ?? []).map(opportunity => this.missedOpportunityPipe.transform(opportunity)).join(', ');

    protected appointmentHasTime(row: OrderGridViewDto): boolean {
        return row.appointmentStatus === OrderAppointmentStatus.ManualAppointmentSet
            || !!(row.sourceLocationAppointmentDate || row.deliveryDateTime || row.deliveryAppointment);
    }
}
